import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  TrendingUp,
  FileText,
  MessageSquare,
  Calendar,
  Leaf,
  Globe,
  GraduationCap,
  Shield,
  Search,
  Users,
  Video,
  ShoppingBag,
  CheckCircle,
  Bot,
  Cpu
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AnimatedSection, FadeIn, AnimatedContainer, AnimatedItem } from "@/components/AnimatedSection";
import SEO from "@/components/SEO";
const caseStudies = [
  {
    id: "autonomous-material-movement",
    icon: Bot,
    title: "Autonomous Material Movement in Manufacturing",
    client: "Manufacturing & Intralogistics Enterprise",
    description: "Deployed Collaborative Robotics' Proxie platform across facility material routes, automating cart transportation, eliminating manual walking, and integrating event-driven intralogistics workflows.",
    tags: ["Autonomous Robotics", "Intralogistics", "AMR", "ROS", "Workflow Automation"],
    color: "bg-blue-50",
    iconColor: "text-[#243b7f]",
    metric: "100% Non-Value-Added Walking Reduced",
  },
  {
    id: "cnc-machine-tending-automation",
    icon: Cpu,
    title: "CNC Machine Tending & Automated Part Handling",
    client: "Industrial Components Manufacturer",
    description: "Automated manual loading and unloading of precision CNC machining cells using Standard Bots RO1 and ROS-based orchestration, optimizing machine utilization and part cycle consistency.",
    tags: ["Collaborative Robotics", "CNC Machine Tending", "Computer Vision", "ROS", "Industrial Automation"],
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    metric: "24/7 Unattended CNC Machine Utilization",
  },
  {
    id: "concrete-curing-automation",
    icon: CheckCircle,
    title: "AI Vision Concrete Curing Automation",
    client: "Leading Infrastructure Firm",
    description: "Deployed a WhatsApp VLM agent across 100+ site groups to automate concrete curing verification, delivering instant in-chat Pass/Fail feedback and replacing manual HQ Excel logging.",
    tags: ["Computer Vision", "VLM", "Construction", "Custom WhatsApp API", "Quality Assurance"],
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    metric: "100+ WhatsApp Groups Automated",
  },
  {
    id: "equipment-ordering-app",
    icon: ShoppingBag,
    title: "E-Commerce Ordering Platform for Site Equipment",
    client: "Leading Construction Group",
    description: "Replaced manual phone-based ordering workflows with a robust mobile catalog, reducing delivery errors and integrating field requisitions.",
    tags: ["E-Commerce", "Procurement", "WhatsApp API", "Workflow Automation"],
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    metric: "Zero Wrong Equipment Deliveries",
  },
  {
    id: "sovereign-mining-detection",
    icon: Video,
    title: "Sovereign Mining Detection & ANPR System",
    client: "State Mining Departments",
    description: "Deployed a fully offline dual-camera barricade monitoring system utilizing YOLOv4-Tiny, DeepSORT, and PaddleOCR to interdict illegal mineral transit.",
    tags: ["Edge AI", "Computer Vision", "ANPR", "Sovereign Infrastructure"],
    color: "bg-slate-50",
    iconColor: "text-[#243b7f]",
    metric: "98% Illegal Transit Interdiction",
  },
  {
    id: "minemitra-chatbot",
    icon: MessageSquare,
    title: "MineMitra: Cosine Similarity Mining Q&A Bot",
    client: "Sovereign Regulatory Authorities",
    description: "Developed a fully local compliance assistant matching natural language queries to regulation databases using text embedding cosine similarity.",
    tags: ["Local RAG", "Embedding Search", "Compliance Bot", "Sovereign AI"],
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    metric: "95% Direct Compliance Query Match",
  },
  {
    id: "face-recognition-attendance",
    icon: Users,
    title: "Biometric Face Recognition & Presence Monitoring",
    client: "Industrial Checkpoints",
    description: "Implemented an on-premise local attendance tracking and cabin presence monitoring system using RetinaNet and ArcFace embedding matching.",
    tags: ["Biometrics", "Face Detection", "ArcFace", "Edge AI"],
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    metric: "99.7% Biometric Verification Accuracy",
  },
  {
    id: "onprem-document-ai",
    icon: Shield,
    title: "Sovereign Document AI & Fraud Detection API",
    client: "Leading National Retail Bank",
    description: "Built a fully on-premise Document Processing API with pixel-level tampering checks, KYC validation, and deep learning OCR.",
    tags: ["On-Premise", "Computer Vision", "Fraud Analysis", "Fintech"],
    color: "bg-blue-50",
    iconColor: "text-blue-600",
    metric: "99.9% Document Processing Accuracy",
  },
  {
    id: "compliance-qa-platform",
    icon: Search,
    title: "Conversational QA & Compliance Audit Platform",
    client: "Global Wealth Management Firm",
    description: "Developed a secure compliance QA tool allowing instant queries over SEC regulations and policies with full citation attribution.",
    tags: ["RAG Engine", "Fintech", "Compliance", "Semantic Search"],
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    metric: "90% Faster Compliance Auditing",
  },
  {
    id: "secure-lms-drm",
    icon: GraduationCap,
    title: "Securing & Scaling Enterprise Learning",
    client: "Global Media & Education Conglomerate",
    description: "Built an enterprise-grade LMS featuring secure DRM content protection, AI-driven assessment generation, mass marketing pipelines, and learner dashboards.",
    tags: ["LMS", "DRM", "Generative AI", "Analytics"],
    color: "bg-cyan-50",
    iconColor: "text-cyan-600",
    metric: "100% Security & 80% Faster Setup",
  },
  {
    id: "fmcg-sop-digitization",
    icon: BarChart3,
    title: "Digitizing S&OP for a Leading FMCG Giant",
    client: "Leading FMCG Company in North America",
    description: "Replaced manual planning with a scalable, data-driven S&OP platform featuring ML-powered demand forecasting across multi-channel FMCG operations.",
    tags: ["S&OP", "FMCG", "Machine Learning"],
    color: "bg-indigo-50",
    iconColor: "text-indigo-600",
    metric: "End-to-End S&OP Transformation",
  },
  {
    id: "sales-operations-digitization",
    icon: TrendingUp,
    title: "Sales & Operations Digitization",
    client: "Global Smartphone Brand",
    description: "Digitizing Sales and Operations Management through an end-to-end centralized platform, eliminating manual reconciliation and enabling AI-ready data ecosystem.",
    tags: ["S&OP", "Digital Transformation", "Analytics"],
    color: "bg-blue-50",
    iconColor: "text-[#243b7f]",
    metric: "Eliminated Manual Reconciliation",
  },
  {
    id: "ai-invoicing-engine",
    icon: FileText,
    title: "AI Invoicing Engine",
    client: "Global Enterprise",
    description: "AI-powered invoicing system to automate financial workflows handling massive volume of invoices in multiple formats and languages.",
    tags: ["IDP", "Computer Vision", "Finance"],
    color: "bg-amber-50",
    iconColor: "text-amber-600",
    metric: "Eliminated Invoice Backlog",
  },
  {
    id: "whatsapp-ai-pitch-bot",
    icon: MessageSquare,
    title: "WhatsApp AI Pitch Bot",
    client: "Startup Ecosystem",
    description: "AI-powered WhatsApp bot enabling regional founders to create professional pitch decks, breaking language barriers.",
    tags: ["Generative AI", "WhatsApp API", "Multilingual"],
    color: "bg-emerald-50",
    iconColor: "text-emerald-600",
    metric: "Massive Founder Onboarding",
  },
  {
    id: "event-brief-automation",
    icon: Calendar,
    title: "Event Brief Automation",
    client: "Events Management Company",
    description: "AI system to automate event brief creation and approvals, transforming manual document-based workflows.",
    tags: ["NLP", "Document AI", "Workflow"],
    color: "bg-purple-50",
    iconColor: "text-purple-600",
    metric: "70% Faster Brief Generation",
  },
  {
    id: "esg-reporting-automation",
    icon: Leaf,
    title: "ESG Reporting Automation",
    client: "Enterprise Corporation",
    description: "AI platform for automated ESG report generation, solving complex reporting requirements with fragmented data.",
    tags: ["ESG", "Document AI", "Compliance"],
    color: "bg-teal-50",
    iconColor: "text-teal-600",
    metric: "70% Faster Report Generation",
  },
  {
    id: "ai-bilingual-chatbot",
    icon: Globe,
    title: "AI Bilingual Chatbot",
    client: "Customer Service Platform",
    description: "AI chatbot enabling multilingual customer support with English and Hindi language capabilities.",
    tags: ["NLP", "Chatbot", "Multilingual"],
    color: "bg-rose-50",
    iconColor: "text-rose-600",
    metric: "60% Faster Query Resolution",
  },
];

export default function CaseStudiesPage() {
  return (
    <div className="overflow-hidden" data-testid="case-studies-page">
      {/* Hero Section */}
      <SEO
        title={"Client Success Stories & Enterprise AI Case Studies | VANCO"}
        description={"Discover real-world enterprise transformations across mining, fintech, construction, FMCG, and government powered by VANCO."}
        path="/case-studies"
      />
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-white via-slate-50 to-blue-50" data-testid="case-studies-hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#243b7f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block px-4 py-2 bg-[#243b7f]/10 text-[#243b7f] rounded-full text-sm font-medium mb-6">
              Success Stories
            </span>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-[#0f172a] font-['Manrope'] mb-6">
              Real Impact,{" "}
              <span className="text-[#243b7f]">Real Results</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Explore how we've helped enterprises transform their operations with AI-powered solutions that deliver measurable business outcomes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Cards Grid */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-white via-slate-50 to-blue-50" data-testid="case-studies-grid">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-[#243b7f]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-0 w-[300px] h-[300px] bg-[#f7c550]/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
          <AnimatedContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.map((study) => (
              <AnimatedItem key={study.id}>
                <Link to={`/case-studies/${study.id}`}>
                  <Card className="h-full bg-white border-slate-100 hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500 hover:-translate-y-2 cursor-pointer group overflow-hidden">
                    <CardContent className="p-0">
                      {/* Header with icon */}
                      <div className={`${study.color} p-6 relative overflow-hidden`}>
                        <div className="flex items-start justify-between">
                          <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                            <study.icon className={`w-7 h-7 ${study.iconColor}`} />
                          </div>
                          <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#243b7f] group-hover:translate-x-1 transition-all duration-300" />
                        </div>

                        {/* Metric badge */}
                        <div className="mt-4">
                          <span className="inline-block px-3 py-1 bg-white/80 rounded-full text-xs font-semibold text-[#243b7f]">
                            {study.metric}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <p className="text-xs text-slate-500 font-medium mb-2">{study.client}</p>
                        <h3 className="text-xl font-bold text-[#0f172a] font-['Manrope'] mb-3 group-hover:text-[#243b7f] transition-colors">
                          {study.title}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">
                          {study.description}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-2">
                          {study.tags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="bg-slate-100 text-slate-600 text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </section>

      {/* Stats Section */}
      <AnimatedSection className="py-24 md:py-32 bg-white" data-testid="case-studies-stats">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <FadeIn>
              <span className="text-[#f7c550] font-semibold text-sm tracking-wide uppercase mb-4 block">
                Our Impact
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-[#0f172a] font-['Manrope']">
                Driving Results Across Industries
              </h2>
            </FadeIn>
          </div>

          <AnimatedContainer className="grid md:grid-cols-4 gap-8">
            {[
              { value: "50+", label: "Global Clients" },
              { value: "32", label: "Active AI Projects" },
              { value: "1M+", label: "Users Engaged" },
              { value: "70%", label: "Avg. Efficiency Gain" },
            ].map((stat) => (
              <AnimatedItem key={stat.label}>
                <div className="text-center p-8 bg-slate-50 rounded-2xl">
                  <div className="text-5xl font-bold text-[#243b7f] font-['Manrope'] mb-2">
                    {stat.value}
                  </div>
                  <div className="text-slate-600 font-medium">{stat.label}</div>
                </div>
              </AnimatedItem>
            ))}
          </AnimatedContainer>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#243b7f]" data-testid="case-studies-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <FadeIn>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white font-['Manrope'] mb-6">
              Let's create your success story
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Join the growing list of enterprises that have transformed their operations with our AI solutions.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#f7c550] hover:bg-[#eab308] text-[#0f172a] rounded-full px-10 py-6 text-lg font-bold transition-all duration-300 hover:scale-105"
                data-testid="case-studies-footer-cta"
              >
                Start Your Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
